import { User as FirebaseUser, signInWithCustomToken } from "firebase/auth";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { StatusCode } from "@types";
import { decrypt, encrypt } from "@utils";
import { client, ExtendedAxiosRequestConfig } from "@api";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  endSignIn,
  getIdentity,
  initAuth,
  startSignIn,
  throwSignInError,
  signOut as signOutRequest,
} from "@store/auth";
import { switchToMaintenanceMode } from "@store/global";
import { FirebaseContextValue } from "./firebase.types";
import { firebaseAuth } from "./firebase.setup";

export interface FirebaseProviderProps {
  children: React.ReactNode;
}

const FirebaseContext = createContext<FirebaseContextValue>({
  firebaseUser: null,
  isLoggedIn: false,
  isInitialized: false,
  isSignInError: false,
  signIn: () => {},
  signOut: () => {},
});

export const FirebaseProvider: React.FC<FirebaseProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();

  const isLoggedInValue = useAppSelector((state) => state.auth.isLoggedIn);
  const isInitialized = useAppSelector((state) => state.auth.isInitialized);
  const isSignInError = useAppSelector((state) => state.auth.isError.signIn);
  const isSignInLoading = useAppSelector((state) => state.auth.isLoading.signIn);
  const customToken = useAppSelector((state) => state.auth.customToken);

  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);

  const isLoggedIn = isLoggedInValue && !isSignInLoading && !isSignInError;

  const signOut = useCallback(async () => {
    try {
      await dispatch(signOutRequest()).unwrap();
    } catch (error) {
      console.error(error);
    } finally {
      firebaseAuth.signOut();
    }
  }, [dispatch]);

  const signIn = useCallback(
    async (token: string) => {
      try {
        dispatch(startSignIn());
        await signInWithCustomToken(firebaseAuth, token);
      } catch (error) {
        console.error(error);
        dispatch(throwSignInError());
        firebaseAuth.signOut();
      } finally {
        dispatch(endSignIn());
      }
    },
    [dispatch]
  );

  const firebaseContextValue = useMemo(
    () => ({
      firebaseUser,
      isLoggedIn,
      isInitialized,
      isSignInError,
      signIn,
      signOut,
    }),
    [firebaseUser, isInitialized, isLoggedIn, isSignInError, signIn, signOut]
  );

  useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged((currentUser) => {
      setFirebaseUser(currentUser);
      dispatch(initAuth(!!currentUser));

      client.interceptors.request.clear();
      client.interceptors.response.clear();

      client.interceptors.request.use(async (config) => {
        try {
          if (currentUser && config.headers) {
            const token = await currentUser.getIdToken();
            config.headers.Authorization = `Bearer ${token}`;
          }

          if ((config as ExtendedAxiosRequestConfig).isEncrypted) {
            (config as ExtendedAxiosRequestConfig).originalData = config.data;
            config.data = encrypt(config.data);
          }

          if ((config as ExtendedAxiosRequestConfig).delay) {
            const delay = new Promise((resolve) => {
              setTimeout(
                () => {
                  resolve(void 0);
                },
                (config as ExtendedAxiosRequestConfig).delay
              );
            });

            return delay.then(() => config);
          }

          return config;
        } catch (e) {
          return Promise.reject(e);
        }
      });

      client.interceptors.response.use(
        (response) => {
          if (
            (response.config as ExtendedAxiosRequestConfig).isEncrypted &&
            response.data?.payload
          ) {
            response.data.payload = decrypt(response.data.payload);
          }

          return response;
        },
        async (error) => {
          if (error.response?.status === StatusCode.Maintenance) {
            dispatch(switchToMaintenanceMode());
            return Promise.reject(error);
          }

          const shouldRetry = (error.config as ExtendedAxiosRequestConfig)?.retryCondition
            ? (error.config as ExtendedAxiosRequestConfig).retryCondition(error.response)
            : true;

          if (error.config?.retry && shouldRetry) {
            error.config.retry -= 1;

            if (error.config.isEncrypted && error.config.originalData) {
              error.config.data = error.config.originalData;
            }

            const delay = new Promise((resolve) => {
              setTimeout(() => {
                resolve(void 0);
              }, error.config.retryDelay || 1000);
            });

            return delay.then(() => client(error.config));
          }

          return Promise.reject(error);
        }
      );
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
    if (customToken) {
      signIn(customToken);
    }
  }, [customToken, signIn]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getIdentity());
    }
  }, [dispatch, isLoggedIn]);

  return (
    <FirebaseContext.Provider value={firebaseContextValue}>{children}</FirebaseContext.Provider>
  );
};

export const useFirebase = (): FirebaseContextValue => useContext(FirebaseContext);
